// src/services/order.service.js
import apiClient from '../apiClient'
import { collectFingerprint } from '@/utils/fingerprint'

/**
 * Creates a new order in the system
 * @param {Object} orderData - The order data
 * @param {string} orderData.type - Order type (OrderCall, OrderGift, OrderContent)
 * @param {string} orderData.clientId - ID of the client making the order
 * @param {string} orderData.creatorId - ID of the creator receiving the order
 * @param {number} [orderData.price] - Optional explicit price (if not using default pricing)
 * @param {Object} orderData.orderDetails - Additional details specific to order type
 * @returns {Promise<Object>} - The created order object
 */
export async function createOrder(orderData) {
    try {
        const { type, clientId, creatorId, price, ...orderDetails } = orderData

        if (!type || !clientId || !creatorId) {
            throw new Error(
                'Missing required order fields: type, clientId, or creatorId'
            )
        }

        // Validate based on order type
        if (type === 'OrderCall' && !orderDetails.callType) {
            throw new Error('Call orders require callType')
        }

        // Collect creator fingerprint data
        let creatorFingerprint = null
        try {
            creatorFingerprint = await collectFingerprint()
        } catch (fingerprintError) {
            console.warn(
                'Failed to collect fingerprint data:',
                fingerprintError
            )
            // Continue with order creation even if fingerprint collection fails
        }

        const orderBody = {
            type,
            clientId,
            creatorId,
            ...(price && { price }), // Only include price if it's provided
            ...(creatorFingerprint && { creatorFingerprint }), // Include fingerprint if available
            ...orderDetails,
        }

        const response = await apiClient.post('/orders', orderBody)

        return response.data.data.order
    } catch (error) {
        console.error('Error creating order:', error)
        throw error
    }
}

/**
 * Creates a call order specifically
 * @param {string} clientId - ID of the client making the call
 * @param {string} creatorId - ID of the creator being called
 * @param {string} callType - Type of call (video or voice)
 * @param {number} [price] - Optional explicit price
 * @returns {Promise<Object>} - The created call order
 */
export async function createCallOrder(
    clientId,
    creatorId,
    callType,
    price = null
) {
    return createOrder({
        type: 'OrderCall',
        clientId,
        creatorId,
        ...(price && { price }),
        callType,
    })
}

/**
 * Creates a message pack order (paid messaging credits) for a specific conversation.
 * @param {string} clientId
 * @param {string} creatorId
 * @param {string} conversationId
 * @param {number} messagesAmount
 * @returns {Promise<Object>} - The created order object
 */
export async function createMessageOrder(
    clientId,
    creatorId,
    conversationId,
    messagesAmount
) {
    return createOrder({
        type: 'OrderMessage',
        clientId,
        creatorId,
        conversationId,
        messagesAmount,
    })
}

/**
 * Gets a order by ID and returns details and user metadata
 * @param {string} orderId - The ID of the order to get
 * @returns {Promise<Object>} - Promise that resolves to an object containing:
 *   - success {boolean} - Whether the request was successful
 *   - data.order {Object} - The order details including:
 *     - orderItem {Object} - Details of the ordered item
 *     - clientId {Object} - Client info with username and profileImage
 *     - creatorId {Object} - Creator info with username and profileImage
 */
export async function getOrderById(orderId) {
    try {
        const response = await apiClient.get(`/orders/${orderId}`)
        return response.data.data.order
    } catch (error) {
        console.error('Error getting order by ID:', error)
        return null
    }
}

/**
 * Pay Content Order with ID
 * @param {string} orderId - The ID of the order to pay
 * @returns {Promise<Object>} - The paid order object
 * @exampleResponse {
  "success": true,
  "message": "Content payment processed successfully",
  "data": {
    "order": {
      "_id": "683a447bf4011c9abeed0214",
      "status": "paid"
    },
    "transaction": {
      "type": "transfer",
      "status": "completed",
      "clientId": "682b87d41cc77bb81fcc6121",
      "creatorId": "682b88411cc77bb81fcc612b",
      "version": 0,
      "totalAmountUSD": 0.4,
      "entryIds": [],
      "reference": {
        "kind": "Content",
        "item": "683a447bf4011c9abeed0214",
        "_id": "683b5ac0c8ce7b1b9705784e"
      },
      "metadata": {
        "orderType": "OrderContent",
        "price": 0.4
      },
      "audit": {
        "statusChanges": []
      },
      "_id": "683b5ac0c8ce7b1b9705784d",
      "createdAt": "2025-05-31T19:38:40.502Z",
      "updatedAt": "2025-05-31T19:38:40.502Z",
      "completedAt": "2025-05-31T19:38:40.502Z",
      "__v": 0
    }
  }
}
 */
export async function payContentOrder(orderId) {
    try {
        // Collect client fingerprint data before initiating payment
        let clientFingerprint = null

        try {
            clientFingerprint = await collectFingerprint()
        } catch (fingerprintError) {
            console.warn(
                'Failed to collect client fingerprint data:',
                fingerprintError
            )
        }

        const payload = clientFingerprint
            ? {
                  clientFingerprint: {
                      ...clientFingerprint,
                      // Ensure timestamp is serialized to ISO string to avoid timezone issues
                      timestamp: clientFingerprint.timestamp
                          ? new Date(clientFingerprint.timestamp).toISOString()
                          : new Date().toISOString(),
                  },
              }
            : {}

        const response = await apiClient.post(
            `/orders/pay/content/${orderId}`,
            payload
        )
        return response.data.data.order
    } catch (error) {
        console.error('Error paying content order:', error)
        throw error
    }
}

/**
 * Pay Message Order with ID (paid message pack purchase)
 * @param {string} orderId - The ID of the message order to pay
 * @returns {Promise<Object>} - The payment result payload (includes messageCredits when available)
 */
export async function payMessageOrder(orderId) {
    try {
        // Collect client fingerprint data before initiating payment
        let clientFingerprint = null

        try {
            clientFingerprint = await collectFingerprint()
        } catch (fingerprintError) {
            console.warn(
                'Failed to collect client fingerprint data:',
                fingerprintError
            )
        }

        const payload = clientFingerprint
            ? {
                  clientFingerprint: {
                      ...clientFingerprint,
                      // Ensure timestamp is serialized to ISO string to avoid timezone issues
                      timestamp: clientFingerprint.timestamp
                          ? new Date(clientFingerprint.timestamp).toISOString()
                          : new Date().toISOString(),
                  },
              }
            : {}

        const response = await apiClient.post(
            `/orders/pay/messages/${orderId}`,
            payload
        )
        return response.data.data
    } catch (error) {
        console.error('Error paying message order:', error)
        throw error
    }
}

export default {
    createOrder,
    createCallOrder,
    createMessageOrder,
    getOrderById,
    payContentOrder,
    payMessageOrder,
}
